"use client";

import { useState } from 'react';
import { getDefaultConfig } from '@/lib/crypto';
import { useAuth } from '@/context/AuthContext';
import LimitReachedDialog from '@/components/LimitReachedDialog';
interface Message {
  role: 'user' | 'assistant';
  content: string;
}
interface FeedbackChatProps {
  sessionId: string;
  onClose: () => void;
}
export default function FeedbackChat({
  sessionId,
  onClose
}: FeedbackChatProps) {
  const [messages, setMessages] = useState<Message[]>([{
    role: 'assistant',
    content: "👋 Hi! I'm your AI career coach. Ask me anything about improving your resume:\n\n• How can I strengthen my experience bullets?\n• What keywords should I add?\n• How do I make my resume more ATS-friendly?\n• Can you rewrite this bullet point?"
  }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const {
    user
  } = useAuth();
  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMessage: Message = {
      role: 'user',
      content: input
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    try {
      const config = getDefaultConfig();
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      if (user) {
        const token = await user.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch('http://localhost:8000/feedback', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          session_id: sessionId,
          question: input,
          conversation_history: messages,
          llm_config: config
        })
      });
      if (response.status === 429) {
        setShowLimitDialog(true);
        setLoading(false);
        return;
      }
      if (!response.ok) throw new Error('Failed to get feedback');
      const data = await response.json();
      const assistantMessage: Message = {
        role: 'assistant',
        content: data.answer
      };
      setMessages(prev_1 => [...prev_1, assistantMessage]);
    } catch (error) {
      console.error('Feedback error:', error);
      setMessages(prev_0 => [...prev_0, {
        role: 'assistant',
        content: '⚠️ Sorry, I encountered an error. Please try again or check your API configuration.'
      }]);
    } finally {
      setLoading(false);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const suggestedQuestions = ["How can I improve my experience section?", "What missing keywords should I add?", "Can you rewrite my summary?", "How do I quantify my achievements?"];
  return <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] md:p-4">
            <div className="glass-panel w-full max-w-3xl h-[100dvh] md:h-[600px] flex flex-col rounded-none md:rounded-2xl border-x-0 md:border bg-white dark:bg-slate-900 md:bg-white/80 md:dark:bg-slate-900/80">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                    <div>
                        <h3 className="text-lg font-semibold">🤖 AI Career Coach</h3>
                        <p className="text-xs text-slate-400">Ask me anything about your resume</p>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                        ✕
                    </button>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((msg, idx) => <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] p-4 rounded-2xl ${msg.role === 'user' ? 'bg-gradient-to-r from-purple-500/30 to-blue-500/30 border border-purple-500/30 ml-auto' : 'bg-white/10 border border-white/10'}`}>
                                <div className="flex items-start gap-2">
                                    <span className="text-lg">
                                        {msg.role === 'user' ? '👤' : '🤖'}
                                    </span>
                                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                                </div>
                            </div>
                        </div>)}

                    {loading && <div className="flex justify-start">
                            <div className="bg-white/10 border border-white/10 p-4 rounded-2xl">
                                <div className="flex items-center gap-2">
                                    <span className="text-lg">🤖</span>
                                    <div className="flex gap-1">
                                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }}></div>
                                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }}></div>
                                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>}
                </div>

                {/* Suggested Questions (only show if few messages) */}
                {messages.length <= 2 && <div className="px-4 pb-2">
                        <p className="text-xs text-slate-400 mb-2">Suggested questions:</p>
                        <div className="flex flex-wrap gap-2">
                            {suggestedQuestions.map((q, idx_0) => <button key={idx_0} onClick={() => setInput(q)} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-full text-xs border border-white/10 transition-colors">
                                    {q}
                                </button>)}
                        </div>
                    </div>}

                {/* Input */}
                <div className="p-4 border-t border-white/10">
                    <div className="flex gap-2">
                        <textarea value={input} onChange={e_0 => setInput(e_0.target.value)} onKeyPress={handleKeyPress} placeholder="Ask me anything... (Shift+Enter for new line)" className="flex-1 bg-white/5 border border-white/10 rounded-xl p-3 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500/50 resize-none max-h-32 text-sm" rows={2} disabled={loading} />
                        <button onClick={handleSend} disabled={loading || !input.trim()} className="px-6 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed rounded-xl font-semibold transition-all flex items-center gap-2">
                            {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <>
                                    <span>Send</span>
                                    <span>→</span>
                                </>}
                        </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                        💡 Tip: Be specific! "How can I improve this bullet: 'Developed features'" → Better answer
                    </p>
                </div>
            </div>

            {/* Daily Limit Reached Dialog */}
            <LimitReachedDialog isOpen={showLimitDialog} onClose={() => setShowLimitDialog(false)} />
        </div>;
}